import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Star, ArrowRight, Send, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const GOOGLE_REVIEW_URL = "https://g.page/r/CXdixRsRC_xhECE/review";

const emptyCheckin = { name: "", location: "", relationship: "", message: "", email: "" };

export default function MemorialPage() {
  const { slug } = useParams();
  const [memorial, setMemorial] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [form, setForm] = useState(emptyCheckin);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSeo({
    title: memorial ? `${memorial.deceased_name} — Livestream · Family Legacy Films` : "Memorial · Family Legacy Films",
    description: memorial ? `A private livestream memorial for ${memorial.deceased_name}. Family and friends who cannot attend can watch and leave a message here.` : "",
  });

  useEffect(() => {
    // Discourage indexing on memorial pages
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { if (meta.parentNode) meta.parentNode.removeChild(meta); };
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get(`/memorial/${slug}`)
      .then((r) => { if (mounted) setMemorial(r.data); })
      .catch(() => { if (mounted) setNotFound(true); });
    api.get(`/memorial/${slug}/checkins`)
      .then((r) => { if (mounted) setCheckins(r.data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/memorial/${slug}/checkins`, form);
      setCheckins((prev) => [{ ...data }, ...prev]);
      setForm(emptyCheckin);
      setSubmitted(true);
      toast.success("Thank you for checking in.");
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-[70vh] pt-40 pb-20 bg-cream text-center px-6" data-testid="memorial-not-found">
        <Heart size={40} style={{ color: "#C9A961" }} className="mx-auto mb-6" />
        <h1 className="font-serif text-4xl mb-4">Memorial not found.</h1>
        <p className="text-warmgrey mb-8">The link you followed may be private or has been removed. If a family member sent you this link, please check that it was typed correctly.</p>
        <Link to="/" className="btn-outline">Return home</Link>
      </div>
    );
  }
  if (!memorial) {
    return <div className="min-h-[70vh] pt-40 text-center text-warmgrey" data-testid="memorial-loading">Loading…</div>;
  }

  return (
    <div className="bg-cream min-h-screen" data-testid="page-memorial">
      {/* Hero — deceased name */}
      <section className="relative pt-40 pb-14 md:pt-48 md:pb-20 bg-secondary text-white text-center overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(201,169,97,0.35) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(201,169,97,0.25) 0%, transparent 55%)" }} />
        <motion.div className="relative max-w-4xl mx-auto px-6"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <p className="eyebrow text-primary mb-5">{memorial.subtitle || "In loving memory"}</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] text-white" style={{ fontStyle: "italic" }}>
            {memorial.deceased_name}
          </h1>
          {memorial.service_date_display && (
            <p className="mt-6 text-white/70 text-sm uppercase tracking-widest">{memorial.service_date_display}</p>
          )}
          <div className="mt-8 mx-auto w-12 h-px bg-primary"></div>
        </motion.div>
      </section>

      {/* Video */}
      <section className="py-14 md:py-20 bg-offwhite">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative w-full rounded-xl overflow-hidden bg-black shadow-[0_20px_50px_rgba(0,0,0,0.15)]" style={{ aspectRatio: "16/9" }}>
            <iframe
              src={memorial.embed_url}
              title={memorial.deceased_name}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid="memorial-video"
            />
          </div>
          {memorial.description && (
            <p className="text-center text-warmgrey text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
              {memorial.description}
            </p>
          )}
        </div>
      </section>

      {/* Check-in form */}
      <section className="py-16 bg-cream">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="eyebrow mb-3">Guest Book</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Check in and leave a message.</h2>
            <p className="text-warmgrey">Let the family know you were here today.</p>
          </div>
          <form onSubmit={submit} className="bg-white p-6 md:p-8 rounded-xl border border-border-warm space-y-4" data-testid="checkin-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Your name *</label>
                <input required data-testid="checkin-name" className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Location</label>
                <input data-testid="checkin-location" className="input-field" placeholder="Suburb, city or country" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">How you knew them</label>
              <input data-testid="checkin-relationship" className="input-field" placeholder="e.g. Daughter, friend, colleague" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">A message for the family</label>
              <textarea data-testid="checkin-message" className="input-field min-h-[110px]" placeholder="A memory, a few words, or simply your love…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Your email *</label>
              <input required type="email" data-testid="checkin-email" className="input-field" placeholder="So we can send you a thank you note" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="checkin-submit">
              {submitting ? "Sending…" : submitted ? "Sent" : (<><Send size={16} /> Check in</>)}
            </button>
          </form>
        </div>
      </section>

      {/* Check-in list */}
      {checkins.length > 0 && (
        <section className="py-8 pb-16 bg-cream">
          <div className="max-w-2xl mx-auto px-6">
            <p className="eyebrow text-center mb-6">Recent check-ins ({checkins.length})</p>
            <div className="space-y-3">
              {checkins.map((c, i) => (
                <div key={i} className="bg-white border border-border-warm rounded-lg p-5" data-testid={`checkin-item-${i}`}>
                  <div className="flex justify-between items-baseline gap-3 mb-1">
                    <p className="font-serif text-lg text-headline">{c.name}</p>
                    <p className="text-xs text-warmgrey-muted whitespace-nowrap">{new Date(c.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <p className="text-xs text-warmgrey-muted mb-2">
                    {c.location && <span>{c.location}</span>}
                    {c.location && c.relationship && <span> · </span>}
                    {c.relationship && <span>{c.relationship}</span>}
                  </p>
                  {c.message && <p className="text-warmgrey text-[15px] leading-relaxed whitespace-pre-wrap">{c.message}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Google review — hardcoded for every memorial page */}
      <section className="py-14 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={20} fill="#C9A961" color="#C9A961" />)}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">If our work helped today, please share a word.</h2>
          <p className="text-warmgrey mb-8">A short Google review helps other families find us in their most difficult hour.</p>
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="google-review-cta">
            <Star size={16} /> Leave a Google review
          </a>
        </div>
      </section>

      {/* Emotional CTA to Legacy Films */}
      <section className="py-24 bg-secondary text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Heart size={30} style={{ color: "#C9A961" }} className="mx-auto mb-6" />
          <p className="eyebrow text-primary mb-4">A Gentle Thought</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 leading-tight">
            While there is still time — record the stories you love.
          </h2>
          <p className="text-white/80 text-lg leading-relaxed mb-4">
            {memorial.cta_copy || (
              <>The voices, the laughter, the wisdom — one day they'll be all your family has left. Before you lose your loved one, let us gently document the best of their story, so your children and grandchildren can hear it, see it, and hold it forever.</>
            )}
          </p>
          <p className="font-serif italic text-white/60 mb-10">"The best time was twenty years ago. The second best time is now."</p>
          <Link to="/legacy-films" className="btn-primary" data-testid="memorial-legacy-cta">
            Discover Family Legacy Films <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
